import {
  AlertTriangle,
  Bug,
  CheckCircle2,
  ClipboardCheck,
  Cloud,
  FileText,
  Gauge,
  GitCompare,
  HelpCircle,
  KeyRound,
  LayoutGrid,
  Lightbulb,
  Lock,
  Network,
  PieChart,
  Search,
  Server,
  ShieldAlert,
  ShieldCheck,
  TrendingUp,
  type LucideIcon,
} from "lucide-react";
import { cn } from "../../ui/utils";

/** Keyword -> icon rules, ordered specific first so the most meaningful match
 *  wins. Used to give each free-text suggestion a topical icon instead of a
 *  generic one. */
const ICON_RULES: Array<[RegExp, LucideIcon]> = [
  [/\b(vuln\w*|cve|exploit|finding)/i, Bug],
  [/\b(risk|threat)/i, AlertTriangle],
  [/\b(trend|over time|history|timeline|monthly|weekly|this week|since|changed|change)/i, TrendingUp],
  [/\b(audit|evidence|readiness|ready)/i, ClipboardCheck],
  [/\b(non[-\s]?compliant|failing|failed|fail|violation|gap)/i, ShieldAlert],
  [/\b(passing|passed|remediat|resolved)/i, CheckCircle2],
  [/\b(control|policy|policies|requirement)/i, ShieldCheck],
  [/\b(asset|inventor|cmdb|device|endpoint|host|server|machine)/i, Server],
  [/\b(framework|catalog|standard|section|group)/i, LayoutGrid],
  [/\b(compare|comparison|vs|versus)/i, GitCompare],
  [/\b(chart|graph|breakdown|distribution|visual|pie|donut|doughnut)/i, PieChart],
  [/\b(access|mfa|identity|login|auth\w*|permission|privileg|role|user)/i, KeyRound],
  [/\b(encrypt\w*|crypto\w*|tls|ssl|certificate|cipher)/i, Lock],
  [/\b(cloud|aws|azure|gcp|s3|bucket)/i, Cloud],
  [/\b(network|firewall|traffic|port|segment)/i, Network],
  [/\b(report|export|document|artifact)/i, FileText],
  [/\b(insight|recommend\w*|suggest\w*|summar\w*|advice)/i, Lightbulb],
  [/\b(posture|compliance|compliant|score|coverage|status)/i, Gauge],
  [/\b(search|find|which|what|show|list|how|where)/i, Search],
];

/** Pick a topical icon for a free-text suggestion, falling back to a neutral
 *  question mark when nothing matches. */
function iconForText(text: string): LucideIcon {
  for (const [re, icon] of ICON_RULES) {
    if (re.test(text)) return icon;
  }
  return HelpCircle;
}

/**
 * A single conversational suggestion chip: a compact, pill-shaped button with a
 * small topical icon and the prompt text. Used for both the empty-state example
 * prompts and the post-answer follow-ups so every suggestion in the assistant
 * shares one lightweight, wrapping style - never a heavy dashboard card. When
 * `icon` is omitted it is derived from the title so each chip gets a topical
 * symbol.
 */
export function SuggestionChip({
  icon,
  title,
  onClick,
  disabled = false,
  className,
}: {
  icon?: LucideIcon;
  title: string;
  onClick: () => void;
  disabled?: boolean;
  className?: string;
}) {
  const Icon = icon ?? iconForText(title);
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={cn(
        "group inline-flex max-w-full items-center gap-1.5 rounded-full border px-3 py-1.5 text-left align-top text-[0.75rem] font-medium leading-snug transition-all duration-200",
        "border-border/70 bg-card/60 text-foreground/85 backdrop-blur-sm",
        "hover:-translate-y-px hover:border-primary/45 hover:bg-primary/[0.06] hover:text-foreground hover:shadow-sm",
        "active:translate-y-0 active:scale-[0.98] motion-reduce:transform-none",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background",
        "dark:border-white/10 dark:bg-white/[0.05] dark:text-foreground/80 dark:hover:border-violet-300/40 dark:hover:bg-white/[0.09]",
        "disabled:cursor-not-allowed disabled:opacity-45 disabled:hover:translate-y-0 disabled:hover:shadow-none",
        className,
      )}
    >
      <Icon
        className="size-3.5 shrink-0 text-primary/70 transition-colors duration-200 group-hover:text-primary"
        strokeWidth={2}
        aria-hidden
      />
      <span className="min-w-0 break-words">{title}</span>
    </button>
  );
}
